import { inject, injectable } from 'inversify';
import { TYPES } from '@/engine/ioc';
import { SceneItemKind } from './interfaces';

import type { ICasePersistent, ICaseState } from './CasePersistance/CasePersistenceHub';
import type { ViewsService } from './Views.service';
import type { IStrategyByKindProvider } from './LoadStateStrategiesProvider';
import type { MeasurementData, viewData } from './DiameterMeasurementsResolver';

export type StateMetadata = Array<viewData>;

@injectable()
export class DistanceMeasurementsResolver implements ICasePersistent {
  private readonly _viewsService: ViewsService;

  private readonly _strategyByKindProvider: IStrategyByKindProvider;

  public constructor(
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.IStrategyByKindProvider) strategyByKindProvider: IStrategyByKindProvider
  ) {
    this._viewsService = viewsService;
    this._strategyByKindProvider = strategyByKindProvider;
  }

  public saveState(): Promise<ICaseState> {
    const views = this._viewsService.views;
    const dataToSave: StateMetadata = [];

    views.forEach((view) => {
      const viewDataToSave: viewData = {
        viewDescriptor: view.getDescriptor(),
        measurements: []
      };

      const addedMeasurements = view.getMeasurements().filter((m) => {
        return (
          (m.kind === SceneItemKind.DistanceMeasurement || m.kind === SceneItemKind.LabeledDistanceMeasurement) &&
          !m.imported
        );
      });

      addedMeasurements?.forEach((m) => {
        const attachmentVm = this._viewsService.currentViewOrThrow.getObjects().find(o => o.descriptor === m.attachments[0]);

        viewDataToSave.measurements.push({
          descriptor: m.descriptor,
          label: m.label,
          kind: m.kind,
          phaseId: m.phaseId,
          syncSliceDescriptor: m.syncSliceDescriptor,
          imagesMode: m.imagesMode,
          zoomFactor: m.zoomFactor,
          attachments: m.attachments,
          details: m.details,
          attachmentData: {
            color: attachmentVm?.color
          }
        });
      });
      dataToSave.push(viewDataToSave);
    });

    const state: ICaseState<StateMetadata> = {
      version: '1.0',
      metadata: dataToSave
    };

    return Promise.resolve(state);
  }

  public loadState(metadata: unknown): Promise<void> {
    const viewsIndicationsData = metadata as StateMetadata;

    viewsIndicationsData.forEach((viewData) => {
      const view = this._viewsService.getViewByDescriptor(viewData.viewDescriptor);
      if (!view) return;

      viewData.measurements
        .filter((m) => {
          return m.kind === SceneItemKind.DistanceMeasurement || m.kind === SceneItemKind.LabeledDistanceMeasurement;
        })
        .forEach((measurementsData: MeasurementData) => {
          const loadStrategy = this._strategyByKindProvider.getStrategy(measurementsData.kind);
          loadStrategy.loadState(view, measurementsData);
        });
    });
    return Promise.resolve();
  }
}
